/* The hero chip row is a guided tour. It advances on its own, and the panel on
   the right RE-DRAWS ITSELF for the selected capability — a chat exchange for
   Ask TeamGrid, score bars for Productivity Scoring, an hour strip for Hourly
   Breakdown, a pipeline for CRM, and so on. Swapping only the words would have
   made ten capabilities look like one.

   `kind` picks the row archetype (rendered in data/heroPanels.js); `items` are
   that archetype's fields. Everything is written as real product output — a
   number, a name, a next step — because generic copy reads as filler. */

export const CAPABILITIES = [
  {
    id: 'org-intelligence',
    label: 'Org Intelligence',
    blurb: 'The living map of how work actually flows',
    kind: 'insight',
    /* event stream: new insights land at the top */
    stream: true,
    team: 2,
    head: { kind: 'flow', stat: '5', unit: 'teams mapped', note: '38 hand-offs traced this week', c: '#469FD8' },
    items: [
      { tag: 'RESOURCES', c: '#469FD8', icon: 'net', msg: '<b>Green Soul</b> is under-staffed — 3 people carry 70% of the load', act: 'Rebalance' },
      { tag: 'DEPENDENCY', c: '#6C59A6', icon: 'link', msg: '<b>Design → Engineering</b> hand-offs wait 2.4 days', act: 'See the chain' },
      { tag: 'SILO', c: '#469FD8', icon: 'grid', msg: '<b>Ops and Sales</b> haven’t exchanged work in 3 weeks', act: 'Open the map' },
      { tag: 'LOAD', c: '#F2A73A', icon: 'bars', msg: '<b>Engineering</b> is at 1.6× the company baseline', act: 'View split' },
    ],
  },
  {
    id: 'ask-teamgrid',
    label: 'Ask TeamGrid',
    blurb: 'Plain-English answers, read from real patterns',
    kind: 'chat',
    stream: true,
    team: 0,
    head: { kind: 'typing', stat: '1.4s', unit: 'median answer', note: 'reads patterns, not dashboards', c: '#6C59A6' },
    items: [
      { q: 'Why did Sales slow down?', a: 'Reply time <b>doubled</b> after the CRM change — leads now sit 6h before routing.', c: '#366EB6', act: 'See evidence' },
      { q: 'Who’s overloaded right now?', a: '<b>3 people</b> hold 70% of Green Soul. I drafted a rebalance.', c: '#6C59A6', act: 'View breakdown' },
      { q: 'What changed since Monday?', a: 'Meetings in Ops are <b>up 41%</b>; two are recurring and unattended.', c: '#F2A73A', act: 'Trim calendar' },
      { q: 'Is this real progress?', a: 'Output is <b>+12%</b> — verified against focus patterns, not raw activity.', c: '#007A6E', act: 'See proof' },
    ],
  },
  {
    id: 'productivity-scoring',
    label: 'Productivity Scoring',
    blurb: 'Fair scores from real signal, not activity theatre',
    kind: 'score',
    team: 0,
    head: { kind: 'dist', stat: '68', unit: 'company score', note: '+4 vs last month', c: '#34A851', values: [12, 26, 41, 34, 22, 9] },
    items: [
      { name: 'Team Alice', team: 'Engineering', score: 82, delta: '+6', c: '#34A851' },
      { name: 'Rahul Mehta', team: 'Sales', score: 74, delta: '+2', c: '#34A851' },
      { name: 'Design pod', team: 'Design', score: 61, delta: '−4', c: '#F2A73A' },
      { name: 'Priya Nair', team: 'Marketing', score: 43, delta: '−35', c: '#F2673A' },
      { name: 'Ops pod', team: 'Operations', score: 71, delta: '+1', c: '#34A851' },
      { name: 'Dev Patel', team: 'Engineering', score: 66, delta: '−2', c: '#F2A73A' },
      { name: 'Sara Iyer', team: 'Sales', score: 79, delta: '+9', c: '#34A851' },
    ],
  },
  {
    id: 'ai-summaries',
    label: 'AI Summaries',
    blurb: 'Every day written up, before you ask',
    kind: 'doc',
    team: 3,
    head: { kind: 'page', stat: '31', unit: 'written today', note: 'nobody typed a word', c: '#8B75D7' },
    items: [
      { title: 'Design — Tuesday', when: 'written 6:02pm', lines: ['Shipped 9 screens for onboarding v2', '2 blocked on copy since Tuesday', 'Handoff to Engineering ready'] },
      { title: 'Company brief — week 32', when: 'written Mon 8:00am', lines: ['4 wins, 2 risks, 1 decision needed', 'Ops meeting load is the main drag'] },
      { title: 'Standup — Engineering', when: 'nobody typed this', lines: ['Auth refactor merged', 'Billing retry still failing on 2 orgs'] },
      { title: 'Sales — Tuesday', when: 'written 6:04pm', lines: ['9 calls logged, 2 demos booked', 'Acme still waiting on the revised quote'] },
      { title: 'Ops — week 32', when: 'written Mon 8:00am', lines: ['Meeting load down 12% after the calendar trim', 'Two recurring calls still unattended'] },
      { title: 'Marketing — Tuesday', when: 'written 6:01pm', lines: ['Campaign brief shipped', 'After-hours work still 3× baseline'] },
    ],
  },
  {
    id: 'hourly-breakdown',
    label: 'Hourly Breakdown',
    blurb: 'The shape of the day, hour by hour',
    kind: 'hours',
    team: 2,
    head: { kind: 'day', stat: '6.2h', unit: 'focus yesterday', note: 'peak 9:40–11:20', c: '#2DBDB8', values: [1,1,2,3,3,2,1,2,3,3,2,1,1,2,2,1] },
    items: [
      { name: 'Engineering', note: 'deep block 9:40–11:20', hours: [1, 2, 3, 3, 3, 2, 1, 2, 2, 1] },
      { name: 'Ops', note: '41% of the week in meetings', hours: [2, 1, 1, 1, 2, 1, 1, 1, 2, 1] },
      { name: 'Design', note: '14 context switches before noon', hours: [1, 2, 1, 3, 1, 2, 1, 3, 2, 2] },
      { name: 'Marketing', note: '3× baseline after 7pm', hours: [1, 1, 2, 2, 2, 2, 3, 3, 3, 2] },
      { name: 'Sales', note: 'calls cluster 11am–1pm', hours: [1, 2, 3, 3, 2, 1, 1, 2, 1, 1] },
      { name: 'Support', note: 'steady all day, no peak', hours: [2, 2, 2, 2, 2, 2, 2, 2, 2, 2] },
      { name: 'Finance', note: 'month-end spike on Fridays', hours: [1, 1, 1, 2, 2, 3, 3, 2, 1, 1] },
    ],
  },
  {
    id: 'email-insights',
    label: 'Email Insights',
    blurb: 'Where client conversations stall',
    kind: 'thread',
    team: 1,
    head: { kind: 'wait', stat: '42', unit: 'threads waiting 48h+', note: 'median first reply 2h 10m', c: '#469FD8', values: [4, 9, 14, 21, 33, 42] },
    items: [
      { who: 'Acme Logistics', subject: 'Rollout across 400 machines', wait: '9d', owner: 'Rahul', hot: true },
      { who: 'Green Soul', subject: 'Renewal — revised quote', wait: '52h', owner: 'Priya', hot: true },
      { who: 'Northwind', subject: 'Security questionnaire', wait: '18h', owner: 'Dev', hot: false },
      { who: 'Blue Yonder', subject: 'Pilot feedback', wait: '2h', owner: 'Sara', hot: false },
      { who: 'Cord4', subject: 'Invoice query — March', wait: '4d', owner: 'Finance', hot: true },
      { who: 'Ergode', subject: 'Contract redlines', wait: '26h', owner: 'Rahul', hot: false },
      { who: 'Uncut Brands', subject: 'Kickoff scheduling', wait: '40m', owner: 'Priya', hot: false },
    ],
  },
  {
    id: 'goals-tasks',
    label: 'Goals & Tasks',
    blurb: 'Progress measured from work, not check-ins',
    kind: 'goal',
    team: 4,
    head: { kind: 'ring', stat: '68', unit: '% of Q3 goals', note: '5 weeks left', c: '#34A851', pct: 68 },
    items: [
      { title: 'Q3 retention +5pts', owner: 'Ops', pct: 68, state: 'On track', c: '#34A851' },
      { title: 'Onboarding revamp', owner: 'Design', pct: 24, state: 'Slipped 2×', c: '#F2673A' },
      { title: 'Cut reply time to 2h', owner: 'Sales', pct: 91, state: 'Almost there', c: '#34A851' },
      { title: 'Migrate billing', owner: 'Engineering', pct: 47, state: 'No owner', c: '#F2A73A' },
      { title: 'Ship CRM v2', owner: 'Product', pct: 82, state: 'On track', c: '#34A851' },
      { title: 'Reduce meeting load 20%', owner: 'Ops', pct: 55, state: 'On track', c: '#34A851' },
      { title: 'Hire 3 engineers', owner: 'People', pct: 33, state: 'Behind', c: '#F2673A' },
    ],
  },
  {
    id: 'app-tracking',
    label: 'App Tracking',
    blurb: 'Where the hours actually go, app by app',
    kind: 'app',
    team: 0,
    head: { kind: 'split', stat: '11.5h', unit: 'tracked today', note: 'across 14 apps', c: '#8B75D7', values: [[53,'#8B75D7'],[30,'#469FD8'],[11,'#366EB6'],[6,'#E0483D']] },
    items: [
      { app: 'Figma', team: 'Design', hrs: 6.1, pct: 100, c: '#8B75D7' },
      { app: 'Slack', team: 'All', hrs: 3.4, pct: 56, c: '#469FD8' },
      { app: 'Jira', team: 'Engineering', hrs: 1.2, pct: 20, c: '#366EB6' },
      { app: '6 unapproved AI tools', team: 'Design', hrs: 0.8, pct: 13, c: '#E0483D', warn: true },
      { app: 'Chrome', team: 'All', hrs: 5.2, pct: 85, c: '#2DBDB8' },
      { app: 'VS Code', team: 'Engineering', hrs: 4.4, pct: 72, c: '#366EB6' },
      { app: 'Gmail', team: 'Sales', hrs: 2.1, pct: 34, c: '#469FD8' },
    ],
  },
  {
    id: 'crm',
    label: 'CRM',
    blurb: 'Pipeline that reflects what actually happened',
    kind: 'deal',
    team: 1,
    head: { kind: 'funnel', stat: '₹42L', unit: 'in open deals', note: '9 with no next step', c: '#366EB6', values: [100, 72, 44, 21] },
    items: [
      { name: 'Acme Logistics', stage: 'Negotiation', value: '₹18L', idle: '14d', c: '#F2673A' },
      { name: 'Green Soul', stage: 'Proposal', value: '₹12L', idle: '3d', c: '#F2A73A' },
      { name: 'Northwind', stage: 'Qualified', value: '₹7L', idle: '1d', c: '#34A851' },
      { name: 'Blue Yonder', stage: 'Won', value: '₹5L', idle: '—', c: '#007A6E' },
      { name: 'Ergode', stage: 'Negotiation', value: '₹9L', idle: '6d', c: '#F2A73A' },
      { name: 'Cord4', stage: 'Proposal', value: '₹4L', idle: '2d', c: '#34A851' },
      { name: 'Uncut Brands', stage: 'Qualified', value: '₹3L', idle: '9d', c: '#F2673A' },
    ],
  },
  {
    id: 'hrms',
    label: 'HRMS',
    blurb: 'People operations, on the same brain',
    kind: 'person',
    team: 4,
    head: { kind: 'week', stat: '96%', unit: 'attendance', note: '2 patterns worth a look', c: '#6C59A6', values: [1,1,1,0,1,1,1,1,1,1,0,1,1,1,1] },
    items: [
      { name: 'Rikesh Kumar', role: 'Ops', note: 'Leave pattern looks unusual', days: [1, 1, 0, 1, 0], c: '#F2A73A' },
      { name: 'Ananya Sharma', role: 'Design', note: 'Full output in 9 days', days: [1, 1, 1, 1, 1], c: '#34A851' },
      { name: 'Dev Patel', role: 'Engineering', note: 'Start time drifted 40m later', days: [1, 1, 1, 0, 1], c: '#469FD8' },
      { name: 'Sara Iyer', role: 'Sales', note: 'On approved leave', days: [1, 0, 0, 1, 1], c: '#6C59A6' },
      { name: 'Arjun Rao', role: 'Support', note: 'Perfect attendance, 6 weeks', days: [1, 1, 1, 1, 1], c: '#34A851' },
      { name: 'Meera Nair', role: 'Finance', note: 'Month-end overtime again', days: [1, 1, 1, 1, 1], c: '#F2A73A' },
      { name: 'Kabir Shah', role: 'Design', note: 'Onboarding week 2', days: [1, 1, 1, 1, 0], c: '#469FD8' },
    ],
  },
];

export const CAPABILITY_IDS = CAPABILITIES.map((c) => c.id);

export function capabilityById(id) {
  return CAPABILITIES.find((c) => c.id === id) || CAPABILITIES[0];
}
